import {
  Literal,
  Number as RNumber,
  Record as RRecord,
  Static,
  Union,
} from "runtypes";

import { getNormalEnum } from "../runtypeEnums.js";

import { AnthropicModelLiteral } from "./anthropicTypes.js";
import { OpenAiChatModelLiteral } from "./openAiTypes.js";

/**
 * Structural exemptions are always exempt (e.g. BYOK, onboarding,
 * support user) and are never controlled by feature flags.
 */
const StructuralExemptBillingReasonLiteral = Union(
  /**
   * The first onboarding project is exempt from billing
   */
  Literal("ONBOARDING_PROJECT"),
  /**
   * Orgs with BYOK do not get charged for usage
   */
  Literal("BYOK"),
  /*
   * We don't bill directly for online evals, but we do track them for statistics
   * and in case we want to bill for them in the future.
   */
  Literal("ONLINE_EVAL"),
  /**
   * We don't bill directly for suggested prompt generation, but we do track them for statistics
   */
  Literal("SUGGESTED_PROMPT_GENERATION"),
  /*
   * We may use support users to debug agents in customer projects, and we don't
   * want to bill customers for that usage.
   */
  Literal("SUPPORT_USER"),
  /*
   * Thread summarization is not billed, but we track for statistics
   */
  Literal("THREAD_SUMMARIZATION"),
  /*
   * Confusion detection is not billed, but we track for statistics
   */
  Literal("CONFUSION_DETECTION"),
  /*
   * Thread topic discovery is not billed, but we track for statistics
   */
  Literal("THREAD_TOPIC_DISCOVERY"),
  /*
   * AI summaries for scheduled run notifications are not billed, but we track them for statistics
   */
  Literal("SCHEDULED_RUN_SUMMARY"),
  /*
   * Thread topic classification is not billed, but we track for statistics
   */
  Literal("THREAD_CLASSIFICATION"),
  /*
   * Memory extraction is not billed, but we track for statistics
   */
  Literal("MEMORY_EXTRACTION"),
  /*
   * Suggestion source grouping into issues is not billed, but we track for statistics
   */
  Literal("SUGGESTION_SOURCE_GROUPING"),
  /* Falling back is expensive since we lose caching, so we exempt threads to avoid annoying our customers */
  Literal("AGENT_PROVIDER_FALLBACK"),
);

/**
 * Feature exemptions are for product workflows that start exempt and eventually
 * get billed via the "ai-billing-feature-exemption" LaunchDarkly flag.
 * Callers always pass through an enum with a target exemption reason,
 * and the flag controls whether we thread it into usage tracking.
 */
export const FeatureExemptBillingReasonLiteral = Union(
  Literal("VISUALIZATION_SUBAGENT"),
  Literal("DATA_APP_SUBAGENT"),
  Literal("REVIEW_AGENT"),
);
export type FeatureExemptBillingReason = Static<
  typeof FeatureExemptBillingReasonLiteral
>;
export const FeatureExemptBillingReason = getNormalEnum(
  FeatureExemptBillingReasonLiteral,
);

export const ExemptBillingReasonLiteral = Union(
  ...StructuralExemptBillingReasonLiteral.alternatives,
  ...FeatureExemptBillingReasonLiteral.alternatives,
);
export type ExemptBillingReason = Static<typeof ExemptBillingReasonLiteral>;
export const ExemptBillingReason = getNormalEnum(ExemptBillingReasonLiteral);

export const RawBillingQuantityDetails = RRecord({
  inputTokens: RNumber,
  outputTokens: RNumber,
  cachedInputTokens: RNumber,
  cacheWrites: RNumber,
});
export type RawBillingQuantityDetails = Static<
  typeof RawBillingQuantityDetails
>;

// Note that if we want to use the same model from different providers, we can prefix the provider to the model name
/**
 * A billable model is a model we have defined a price for how much we charge customers to use
 */
export const BillableModel = Union(
  AnthropicModelLiteral,
  OpenAiChatModelLiteral,
);
export type BillableModel = Static<typeof BillableModel>;
